import { Row } from "@tanstack/react-table";
import { FileText, Folder, LayoutGrid, PencilLine, Trash2 } from "lucide-react";
import { useSelector } from "react-redux";
import { toast } from "sonner";

import { useProductDetailContext } from "../context/ProductDetailContext";

import { useProductDetailTableColumns } from "./ProductDetailTableColumns";

import { CustomTable } from "@/components/reusables/data-table/CustomTable";
import { ToolsConstants, ToolsKeys } from "@/constants/tools.constant";
import useQueryID from "@/hooks/useQueryId";
import { GlobalRowDataTypes } from "@/interfaces/global-table.interface";
import { Permissions } from "@/interfaces/globals";
import { SubProductsItem } from "@/interfaces/tools.interface";
import { hasWritePermission } from "@/redux/slices/permissionSlice";

interface ProductDetailTableProps {
  toggleAddSubProductModal: () => void;
  toggleEditSubProductModal: () => void;
  toggleViewItems: () => void;
  toggleViewSubProduct: () => void;
  setSubProductDetailLog: React.Dispatch<
    React.SetStateAction<{
      isOpen: boolean;
      itemName: string;
      productId: string;
      subProductId: string;
    } | null>
  >;
}

const ProductDetailTable = ({
  toggleAddSubProductModal,
  toggleEditSubProductModal,
  toggleViewItems,
  setSubProductDetailLog,
  toggleViewSubProduct,
}: ProductDetailTableProps): JSX.Element => {
  const { productDetails } = useProductDetailContext();
  const { set } = useQueryID(ToolsKeys.SUB_PRODUCT_ID);

  const getActionOptions = (
    row: Row<SubProductsItem>,
  ): ActionButtonDataTypes[] => [
    {
      icon: LayoutGrid,
      name: "View Items",
      onclick: (): void => {
        const selectedSubProduct = productDetails?.subProducts?.find(
          (subProduct) => subProduct.id === row.original.id,
        );
        localStorage.setItem(
          ToolsKeys.SUB_PRODUCT_NAME,
          selectedSubProduct?.name ?? "",
        );
        set(row.original.id);
        toggleViewItems();
      },
    },
    {
      icon: Folder,
      name: "Subproduct Details",
      onclick: (): void => {
        set(row.original.id);
        toggleViewSubProduct();
      },
    },
    {
      icon: FileText,
      name: "View Log",
      onclick: (): void => {
        setSubProductDetailLog({
          isOpen: true,
          productId: productDetails.id ?? "",
          itemName: row.original?.name ?? "",
          subProductId: row.original?.id ?? "",
        });
      },
    },
    {
      icon: PencilLine,
      name: "Edit",
      onclick: (): void => {
        set(row.original?.id);
        toggleEditSubProductModal();
      },
    },
    {
      icon: Trash2,
      name: "Move to Trash",
      isTrash: true,
      onclick: (): void => {
        toast.info("Edit Ticket");
      },
    },
  ];

  const writePermissions = useSelector(hasWritePermission(Permissions.TOOLS));

  const columns = useProductDetailTableColumns({
    getActionOptions,
    writePermissions,
  });

  return (
    <CustomTable
      columns={columns}
      data={productDetails?.subProducts ?? []}
      empty={{}}
      title={ToolsConstants.PRODUCTS.SUBPRODUCTS}
      hasWritePermission={writePermissions}
      functions={{
        search: ToolsConstants.PRODUCTS.SUBPRODUCTS,
        isExport: true,
        columnToggle: true,
        add: {
          label: ToolsConstants.PRODUCTS.ADD_SUB_PRODUCT,
          handleClick: toggleAddSubProductModal,
        },
        onRowClick: (id) => {
          const selectedSubProduct = productDetails?.subProducts?.find(
            (subProduct) => subProduct.id === id,
          );
          localStorage.setItem(
            ToolsKeys.SUB_PRODUCT_NAME,
            selectedSubProduct?.name ?? "",
          );
          set(id);
          toggleViewItems();
        },
        RightClickData: (
          row: GlobalRowDataTypes<SubProductsItem>,
        ): ActionButtonDataTypes[] =>
          getActionOptions(row as Row<SubProductsItem>),
      }}
      hide={{
        functions: true,
        filters: true,
      }}
    />
  );
};

export default ProductDetailTable;
